import { useState } from 'react';
import { Modal } from './Modal';
import { useCopyToClipboard } from './CopyButton';
import { exportToFile, exportToSwarm, BackupProgress } from '../services/account/backup';
import { hashPassword, saveAccount } from '../services/storage';
import type { Account } from '../types';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: Account;
}

type ExportTab = 'file' | 'swarm';

const progressLabels: Record<BackupProgress, string> = {
  'deriving-key': 'Deriving encryption key...',
  'encrypting': 'Encrypting account data...',
  'decrypting': 'Decrypting...',
  'verifying': 'Verifying...',
  'uploading': 'Uploading to Swarm...',
  'downloading': 'Downloading from Swarm...',
  'looking-up-ens': 'Looking up ENS...',
  'updating-ens': 'Updating ENS record...',
};

export function ExportModal({ isOpen, onClose, account }: ExportModalProps) {
  const [activeTab, setActiveTab] = useState<ExportTab>('file');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [progress, setProgress] = useState<BackupProgress | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [swarmResult, setSwarmResult] = useState<{ hash?: string; warning?: string } | null>(null);
  const { copy, isCopied } = useCopyToClipboard();
  const [currentAccount, setCurrentAccount] = useState(account);

  // Check if account needs password setup
  const needsPassword = !currentAccount.passwordHash;

  const handleClose = () => {
    // Reset state when closing
    setPassword('');
    setConfirmPassword('');
    setError('');
    setProgress(null);
    setSwarmResult(null);
    onClose();
  };

  // Set password for accounts that don't have one
  const handleSetPassword = async () => {
    if (!password) {
      setError('Please enter a password');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setIsExporting(true);
    setProgress('deriving-key');

    try {
      const newPasswordHash = await hashPassword(password);
      const updatedAccount: Account = {
        ...currentAccount,
        passwordHash: newPasswordHash,
      };
      saveAccount(updatedAccount);
      setCurrentAccount(updatedAccount);
      // Now proceed with export automatically
      setProgress(null);
      setIsExporting(false);
    } catch (err) {
      setError('Failed to set password');
      setIsExporting(false);
      setProgress(null);
    }
  };

  const handleExportToFile = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setError('');
    setIsExporting(true);
    setProgress(null);

    try {
      await exportToFile(currentAccount, password, {
        onProgress: setProgress,
      });
      // Success - file download triggered
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      if (message.includes('Incorrect password')) {
        setError('Incorrect password');
      } else {
        setError(message);
      }
    } finally {
      setIsExporting(false);
      setProgress(null);
    }
  };

  const handleExportToSwarm = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setError('');
    setIsExporting(true);
    setProgress(null);
    setSwarmResult(null);

    try {
      const result = await exportToSwarm(currentAccount, password, {
        onProgress: setProgress,
      });

      if (result.success) {
        setSwarmResult({
          hash: result.swarmHash,
          warning: result.error, // May contain warning about ENS update failure
        });
      } else {
        setError(result.error || 'Export failed');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      if (message.includes('Incorrect password')) {
        setError('Incorrect password');
      } else {
        setError(message);
      }
    } finally {
      setIsExporting(false);
      setProgress(null);
    }
  };

  const handleExport = () => {
    if (activeTab === 'file') {
      handleExportToFile();
    } else {
      handleExportToSwarm();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Export Account">
      <div className="export-modal">
        {/* Tabs */}
        <div className="export-tabs">
          <button
            className={`export-tab ${activeTab === 'file' ? 'active' : ''}`}
            onClick={() => setActiveTab('file')}
            disabled={isExporting}
          >
            To File
          </button>
          <button
            className={`export-tab ${activeTab === 'swarm' ? 'active' : ''}`}
            onClick={() => setActiveTab('swarm')}
            disabled={isExporting}
          >
            To Swarm
          </button>
        </div>

        {/* Content */}
        <div className="export-content">
          {activeTab === 'swarm' && !swarmResult && (
            <div className="export-warning">
              <strong>Note:</strong> Swarm backups expire after ~7 days unless the stamp is topped up.
              Download a file backup for long-term storage.
            </div>
          )}

          {swarmResult ? (
            <div className="export-success">
              <div className="export-success-icon">&#10003;</div>
              <div className="export-success-message">
                Backup uploaded successfully!
              </div>
              {swarmResult.hash && (
                <div
                  className="export-hash clickable"
                  onClick={() => copy(swarmResult.hash!, 'hash')}
                  title="Click to copy"
                >
                  <span className="export-hash-label">
                    {isCopied('hash') ? 'Copied!' : 'Swarm Hash (click to copy):'}
                  </span>
                  <code className="export-hash-value">{swarmResult.hash}</code>
                </div>
              )}
              {swarmResult.warning && (
                <div className="export-warning" style={{ marginTop: 16 }}>
                  {swarmResult.warning}
                </div>
              )}
              <button className="btn btn-primary" onClick={handleClose} style={{ marginTop: 24 }}>
                Done
              </button>
            </div>
          ) : needsPassword ? (
            /* Password setup step for wallet accounts without password */
            <>
              {error && <div className="modal-error">{error}</div>}

              <div className="export-warning" style={{ marginBottom: 16 }}>
                <strong>Password Required</strong>
                <p style={{ margin: '8px 0 0' }}>
                  Your account was created without a password. Set a password to enable export.
                </p>
              </div>

              <input
                type="password"
                placeholder="New password (min 6 characters)"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="modal-input"
                disabled={isExporting}
              />

              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="modal-input"
                style={{ marginTop: 12 }}
                disabled={isExporting}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !isExporting) {
                    handleSetPassword();
                  }
                }}
              />

              {progress && (
                <div className="export-progress">
                  <div className="export-progress-spinner" />
                  <span>{progressLabels[progress]}</span>
                </div>
              )}

              <div className="modal-actions">
                <button
                  className="btn btn-outline"
                  onClick={handleClose}
                  disabled={isExporting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-white"
                  onClick={handleSetPassword}
                  disabled={isExporting || !password || !confirmPassword}
                >
                  {isExporting ? 'Setting password...' : 'Set Password'}
                </button>
              </div>
            </>
          ) : (
            <>
              {error && <div className="modal-error">{error}</div>}

              <p className="export-description">
                {activeTab === 'file'
                  ? 'Download an encrypted backup file. Keep this file safe - you\'ll need it and your password to restore your account.'
                  : 'Upload an encrypted backup to Swarm and store the hash in ENS. You can restore from any device using your username and password.'}
              </p>

              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="modal-input"
                disabled={isExporting}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !isExporting) {
                    handleExport();
                  }
                }}
              />

              {progress && (
                <div className="export-progress">
                  <div className="export-progress-spinner" />
                  <span>{progressLabels[progress]}</span>
                </div>
              )}

              <div className="modal-actions">
                <button
                  className="btn btn-outline"
                  onClick={handleClose}
                  disabled={isExporting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-white"
                  onClick={handleExport}
                  disabled={isExporting || !password}
                >
                  {isExporting
                    ? 'Exporting...'
                    : activeTab === 'file'
                    ? 'Download Backup'
                    : 'Upload to Swarm'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
